import { Injectable } from '@angular/core';
import { normalizeApiBaseUrl } from 'src/app/shared/utils/api-base-url';
import { Store } from '@ngrx/store';
import * as fromLedger from 'src/app/core/stores/ledger/ledger.selector';
import * as LedgerActions from 'src/app/core/stores/ledger/ledger.actions';
import * as fromItem from 'src/app/core/stores/item/item.selector';
import * as ItemActions from 'src/app/core/stores/item/item.actions';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Notifications } from 'src/app/models/common/Notifications';
import { RuntimeConfigService } from './runtime-config.service';
import { Actions, ofType } from '@ngrx/effects';

@Injectable({
  providedIn: 'root'
})
export class AppService {

  private notificationSubject = new BehaviorSubject<Notifications[]>(null);
  notificationSubject$ = this.notificationSubject.asObservable();

  private dataSource = new BehaviorSubject<any>(null);
  currentData$ = this.dataSource.asObservable();

  constructor(
    private store: Store,
    private http: HttpClient,
    private runtimeConfigService: RuntimeConfigService,
    private actions$: Actions // <-- Inject Actions
  ) {}

  loadInitialData(companyID: number, financialYear: string): Observable<boolean> {
    if(this.runtimeConfigService.isElectron) {
      this.getDefaultIp().then(ip => {
        localStorage.setItem('defaultIP', ip ? ip : '127.0.0.1');
      });
    }

    // Dispatch actions
    this.store.dispatch(LedgerActions.loadLedgers());
    this.store.dispatch(ItemActions.loadItems());
    this.store.dispatch(ItemActions.loadCompanyLedgers());

    // Wait for all success actions
    return forkJoin([
      this.actions$.pipe(ofType(LedgerActions.loadLedgersSuccess), take(1)),
      this.actions$.pipe(ofType(ItemActions.loadItemsSuccess), take(1)),
      this.actions$.pipe(ofType(ItemActions.loadCompanyLedgersSuccess), take(1)),
      this.GetOverdueBills().pipe(catchError(() => of([]))),
      this.GetLatestSignalRChangeId().pipe(catchError(() => of('0')))
    ]).pipe(
      map(() => true)
    );
  }

  getDefaultIp(): Promise<string> {
    const { ipcRenderer } = window.require('electron');
    return ipcRenderer.invoke('get-default-ip');
  }

  getAllLedgers(companyID: number, financialYear: string): Observable<any> {
    this.store.dispatch(LedgerActions.loadLedgers());
    return this.store.select(fromLedger.selectAllLedgers(companyID, financialYear)).pipe(take(1));
  }

  getAllItems(companyID: number, financialYear: string): Observable<any> {
    this.store.dispatch(ItemActions.loadItems());
    return this.store.select(fromItem.selectAllItems(companyID, financialYear)).pipe(take(1));
  }

  getAllItemCompanyLedgers(companyID: number, financialYear: string): Observable<any> {
    this.store.dispatch(ItemActions.loadCompanyLedgers());
    return this.store.select(fromItem.selectAllItemCompanyLedgers(companyID, financialYear)).pipe(take(1));
  }

  GetOverdueBills(): Observable<Notifications[]> {
    return this.http.get<Notifications[]>(`${normalizeApiBaseUrl(environment.ASquareAPIURL)}Notification/overdue-bills`).pipe(
      tap(result => {
        this.notificationSubject.next(result);
      })
    );
  }

  GetLatestSignalRChangeId(): Observable<string> {
    return this.runtimeConfigService.apiUrl$.pipe(
      take(1),
      switchMap(url =>
        this.http.get<string>(`${normalizeApiBaseUrl(url)}SignalRChanges/latest-signal-r-change-id`).pipe(
          tap(result => {
            localStorage.setItem('changeId', result);
          })
        )
      )
    );
  }

  updateData(data: any) {
    this.dataSource.next(data);
  }
}
